import { IContentElement, IQuestionConfig } from 'src/app/ui-testrunner/models';
import {
  getMatrix,
  getScoringOptionKeys,
  IMatrixRowHeader,
  IScoreMatrix,
  scoreMatrixElementTypes,
} from '../../config-score-matrix/models';
import { getQuestionDeepElements } from './content-analysis';
import {
  getScoreMatrixFixSourceCheckIds,
  getScoreMatrixFixSources,
  ITEM_MATRIX_AUDIT_SLUG,
  ITEM_MATRIX_AUTOFIX_CHECK_IDS,
  ITEM_MATRIX_AUTOFIX_SLUG,
  ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID,
  SCORE_MATRIX_CHECK_REPAIRS,
  SCORE_MATRIX_FILL_IN_AUDIT_SLUG,
  SCORE_MATRIX_FILL_IN_AUTOFIX_SLUG,
  scoreMatrixCheckRepairBySlug,
  withScoreMatrixAutoFix,
} from './score-matrix-autofix-default';

export {
  getScoreMatrixFixSourceCheckIds,
  getScoreMatrixFixSources,
  ITEM_MATRIX_AUDIT_SLUG,
  ITEM_MATRIX_AUTOFIX_CHECK_IDS,
  ITEM_MATRIX_AUTOFIX_SLUG,
  ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID,
  SCORE_MATRIX_CHECK_REPAIRS,
  SCORE_MATRIX_FILL_IN_AUDIT_SLUG,
  SCORE_MATRIX_FILL_IN_AUTOFIX_SLUG,
  scoreMatrixCheckRepairBySlug,
  withScoreMatrixAutoFix,
};

/** @ticket https://bubo.vretta.com/vea/project-management/vretta-project-notes/eqao/authoring/-/work_items/1938 */

/** The fill-in only writes `scoreMatrix`; anything else changing in an item is flagged in the dry run (authoring#1948) */
export const SCORE_MATRIX_FIX_RISK_OPTIONS = { expectedPathPattern: /^scoreMatrix$/ };

/**
 * Element-level scoring settings copied onto every matrix row in September 2026 (00938737e2, cc1debd691).
 * Rows confirmed before then do not carry them, which alone marks those matrices as outdated.
 */
export const ROW_SCORING_SETTING_KEYS = [
  'scoreWeight',
  'enableProportionalScoring',
  'scoreMode',
  'maxCheckedPerRow',
  'maxCheckedPerCol',
  'isMaxDenoScoring',
  'isMustHaveOnePerRow',
  'isMustHaveOnePerCol',
  'isMultiSelect',
  'maxOptions',
  'isEnablePartialweights',
  'isOptional',
];

export enum EMatrixReconfirmClass {
  CURRENT = 'CURRENT',
  RECONFIRM_SAFE = 'RECONFIRM_SAFE',
  UNCONFIRMED = 'UNCONFIRMED',
  CHANGED = 'CHANGED',
}

export interface IMatrixClassification {
  cls: EMatrixReconfirmClass;
  reasons: string[];
}

export interface IMatrixReconfirmEntry extends IMatrixClassification {
  questionId: number;
  label: string;
  lang: string;
  entryId: number | string;
  elementType: string;
  /** never confirmed and no rows yet (stage 1 still to do); false = initialized, only the confirmation is missing */
  neverInitialized?: boolean;
}

export interface IMatrixReconfirmPlan {
  entries: IMatrixReconfirmEntry[];
  counts: Record<EMatrixReconfirmClass, number>;
  /** item labels per class, each label once */
  labels: Record<EMatrixReconfirmClass, string[]>;
}

export interface IMatrixReconfirmResult {
  reconfirmed: number;
  confirmed: number;
  regenerated: number;
  skipped: number;
}

export interface IMatrixReconfirmOptions {
  includeUnconfirmed: boolean;
  /** with includeUnconfirmed: confirm never-confirmed matrices only on the items this accepts (e.g. items with psych stats) */
  unconfirmedFilter?: (question: IQuestionConfig) => boolean;
  /** outdated matrices whose scoring changed: regenerate from the current config and confirm (authoring#1940) */
  includeChanged?: boolean;
  /** only write never-initialized matrices (no rows yet), not other unconfirmed ones */
  initializeOnly?: boolean;
  /**
   * Second stage only: confirm matrices that already have rows, without rebuilding them. Confirmed only when
   * the stored matrix equals what the answer key generates; any that differ are left for review.
   */
  confirmInitialized?: boolean;
}

const LANGS = ['en', 'fr'];

const isDefined = (v: any) => v !== undefined;

/** JSON form with sorted keys and undefined values dropped, i.e. what survives a save/reload */
const canonical = (value: any): string => JSON.stringify(normalize(value));
const normalize = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(v => (v === undefined ? null : normalize(v)));
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).sort().forEach(k => {
      if (isDefined(value[k])) {
        out[k] = normalize(value[k]);
      }
    });
    return out;
  }
  return value;
};

const definedKeys = (row: any): string[] => Object.keys(row ?? {}).filter(k => isDefined(row[k]));

const isScoringOptionEqual = (prev: any, curr: any, elementType: string): boolean => {
  return getScoringOptionKeys(elementType).every(k => {
    const a = prev?.[k];
    const b = curr?.[k];
    return a === b || (!a && !b);
  });
};

/** Differences between stored and regenerated rows; `hasSettingDrift` when only the September keys are missing */
const diffRows = (stored: IMatrixRowHeader[], generated: IMatrixRowHeader[], elementType: string) => {
  const reasons: string[] = [];
  let hasSettingDrift = false;
  if ((stored?.length ?? 0) !== (generated?.length ?? 0)) {
    reasons.push(`row count ${stored?.length ?? 0} -> ${generated?.length ?? 0}`);
    return { reasons, hasSettingDrift };
  }
  generated.forEach((gen, idx) => {
    const prev = stored[idx] ?? <IMatrixRowHeader>{};
    const rowNum = idx + 1;
    definedKeys(gen).forEach(key => {
      if (key === 'option') {
        if (!isScoringOptionEqual(prev.option, gen.option, elementType)) {
          reasons.push(`row ${rowNum} option id`);
        }
      } else if (!isDefined(prev[key])) {
        if (ROW_SCORING_SETTING_KEYS.includes(key)) {
          hasSettingDrift = true;
        } else {
          reasons.push(`row ${rowNum} ${key} added`);
        }
      } else if (canonical(prev[key]) !== canonical(gen[key])) {
        reasons.push(`row ${rowNum} ${key}`);
      }
    });
    definedKeys(prev).forEach(key => {
      if (key !== 'option' && !isDefined(gen[key])) {
        reasons.push(`row ${rowNum} ${key} removed`);
      }
    });
  });
  return { reasons, hasSettingDrift };
};

/** Differences between a stored (not yet confirmed) matrix with rows and the one the answer key generates */
export const storedMatrixDiffersFromKey = (element: IContentElement): string[] => {
  const stored: IScoreMatrix = element.scoreMatrix;
  let generated;
  try {
    generated = getMatrix(element);
  } catch (e) {
    return ['matrix could not be rebuilt'];
  }
  const reasons: string[] = [];
  if (canonical(stored?.columns ?? []) !== canonical(generated.columns)) { reasons.push('columns'); }
  if (canonical(stored?.values ?? []) !== canonical(generated.values)) { reasons.push('values'); }
  reasons.push(...diffRows(stored?.rows ?? [], generated.rows, element.elementType).reasons);
  return reasons;
};

/** Compare an element's stored score matrix against the one its current config would generate */
export const classifyScoreMatrixElement = (element: IContentElement): IMatrixClassification => {
  const stored: IScoreMatrix = element.scoreMatrix;
  if (!stored || !stored.isConfirmed) {
    return { cls: EMatrixReconfirmClass.UNCONFIRMED, reasons: [] };
  }
  let generated;
  try {
    generated = getMatrix(element);
  } catch (e) {
    return { cls: EMatrixReconfirmClass.CHANGED, reasons: ['matrix could not be rebuilt'] };
  }
  const reasons: string[] = [];
  if (canonical(stored.columns ?? []) !== canonical(generated.columns)) {
    reasons.push('columns');
  }
  if (canonical(stored.values ?? []) !== canonical(generated.values)) {
    reasons.push('values');
  }
  const rowDiff = diffRows(stored.rows ?? [], generated.rows, element.elementType);
  reasons.push(...rowDiff.reasons);
  if (reasons.length) {
    return { cls: EMatrixReconfirmClass.CHANGED, reasons };
  }
  if (rowDiff.hasSettingDrift || !stored.isUpdated) {
    return { cls: EMatrixReconfirmClass.RECONFIRM_SAFE, reasons: [] };
  }
  return { cls: EMatrixReconfirmClass.CURRENT, reasons: [] };
};

export const getScoreMatrixElements = (question: IQuestionConfig, lang: string): IContentElement[] => {
  if (lang !== 'en' && !question?.langLink) {
    return [];
  }
  return getQuestionDeepElements(question, lang)
    .filter(el => scoreMatrixElementTypes.includes(<any>el?.elementType));
};

/** Score matrix exists but was never built from the answer key (no rows). */
export const isScoreMatrixNeverInitialized = (element: IContentElement): boolean => {
  const stored = element.scoreMatrix;
  return !stored?.isConfirmed && !(stored?.rows?.length);
};

export const countInitializedUnconfirmedMatrices = (questions: IQuestionConfig[]): number => {
  let n = 0;
  (questions ?? []).forEach(question => {
    LANGS.forEach(lang => {
      getScoreMatrixElements(question, lang).forEach(element => {
        if (!element.scoreMatrix?.isConfirmed && !isScoreMatrixNeverInitialized(element)) { n++; }
      });
    });
  });
  return n;
};

export const countNeverInitializedMatrices = (questions: IQuestionConfig[]): number => {
  let n = 0;
  (questions ?? []).forEach(question => {
    LANGS.forEach(lang => {
      getScoreMatrixElements(question, lang).forEach(element => {
        if (isScoreMatrixNeverInitialized(element)) { n++; }
      });
    });
  });
  return n;
};

const emptyByClass = <T>(make: () => T): Record<EMatrixReconfirmClass, T> => ({
  [EMatrixReconfirmClass.CURRENT]: make(),
  [EMatrixReconfirmClass.RECONFIRM_SAFE]: make(),
  [EMatrixReconfirmClass.UNCONFIRMED]: make(),
  [EMatrixReconfirmClass.CHANGED]: make(),
});

/** Classify every score matrix (both languages) on the given items without changing anything */
export const planScoreMatrixReconfirm = (questions: IQuestionConfig[]): IMatrixReconfirmPlan => {
  const entries: IMatrixReconfirmEntry[] = [];
  const counts = emptyByClass(() => 0);
  const labels = emptyByClass<string[]>(() => []);
  const seen = new Set<number>();
  (questions ?? []).forEach(question => {
    if (!question || seen.has(question.id)) {
      return;
    }
    seen.add(question.id);
    LANGS.forEach(lang => {
      getScoreMatrixElements(question, lang).forEach(element => {
        const { cls, reasons } = classifyScoreMatrixElement(element);
        entries.push({
          questionId: question.id,
          label: question.label,
          lang,
          entryId: element.entryId,
          elementType: element.elementType,
          cls,
          reasons,
          neverInitialized: isScoreMatrixNeverInitialized(element),
        });
        counts[cls]++;
        if (!labels[cls].includes(question.label)) {
          labels[cls].push(question.label);
        }
      });
    });
  });
  return { entries, counts, labels };
};

const writeGeneratedMatrix = (element: IContentElement) => {
  const { rows, columns, values } = JSON.parse(JSON.stringify(getMatrix(element)));
  element.scoreMatrix = {
    ...(element.scoreMatrix ?? {}),
    rows,
    columns,
    values,
    isConfirmed: true,
    isUpdated: true,
  };
};

/**
 * Re-confirm matrices whose answer key is unchanged (rows refreshed, confirmation kept).
 * Never-confirmed matrices are confirmed from the current config only with `includeUnconfirmed`.
 * Matrices whose scoring changed are left for the author to review and confirm, unless `includeChanged`
 * regenerates them from the current config (the same as pressing Confirm in the score matrix panel).
 */
export const applyScoreMatrixReconfirm = (question: IQuestionConfig, opts: IMatrixReconfirmOptions): IMatrixReconfirmResult => {
  const result: IMatrixReconfirmResult = { reconfirmed: 0, confirmed: 0, regenerated: 0, skipped: 0 };
  LANGS.forEach(lang => {
    getScoreMatrixElements(question, lang).forEach(element => {
      const { cls } = classifyScoreMatrixElement(element);
      if (cls === EMatrixReconfirmClass.RECONFIRM_SAFE) {
        writeGeneratedMatrix(element);
        result.reconfirmed++;
      } else if (cls === EMatrixReconfirmClass.UNCONFIRMED && opts.confirmInitialized && !isScoreMatrixNeverInitialized(element)) {
        // second stage: confirm what is already there when it still matches the key, otherwise leave it for review
        if (storedMatrixDiffersFromKey(element).length) {
          result.skipped++;
          return;
        }
        element.scoreMatrix = { ...element.scoreMatrix, isConfirmed: true, isUpdated: true };
        result.confirmed++;
      } else if (cls === EMatrixReconfirmClass.UNCONFIRMED && opts.includeUnconfirmed && (!opts.unconfirmedFilter || opts.unconfirmedFilter(question))) {
        if (opts.initializeOnly && !isScoreMatrixNeverInitialized(element)) {
          result.skipped++;
          return;
        }
        writeGeneratedMatrix(element);
        result.confirmed++;
      } else if (cls === EMatrixReconfirmClass.CHANGED && opts.includeChanged) {
        try {
          writeGeneratedMatrix(element);
          result.regenerated++;
        } catch (e) { // matrix could not be rebuilt from the current config
          result.skipped++;
        }
      } else if (cls !== EMatrixReconfirmClass.CURRENT) {
        result.skipped++;
      }
    });
  });
  return result;
};

export interface IMcqOptionIssue {
  lang: string;
  entryId: number | string;
  detail: string;
}

/**
 * Checks each MCQ's options against its saved score matrix: no blank or empty options, a keyed answer
 * exists (exactly one when single select), and every matrix row matches its option's correct flag.
 * Read only; used after an auto-heal to list the items an author still needs to look at (authoring#1959).
 */
export const validateMcqOptionsAgainstMatrix = (question: IQuestionConfig): IMcqOptionIssue[] => {
  const issues: IMcqOptionIssue[] = [];
  LANGS.forEach(lang => {
    getScoreMatrixElements(question, lang)
      .filter(el => el.elementType === 'mcq')
      .forEach((element: any) => {
        const add = (detail: string) => issues.push({ lang, entryId: element.entryId, detail });
        const options: any[] = element.options ?? [];
        const named = (idx: number) => `option ${idx + 1}`;
        if (!options.length) {
          add('no options');
          return;
        }
        options.forEach((option, idx) => {
          if (!option) { add(`${named(idx)} is blank`); }
          else if (!`${option.caption ?? ''}${option.content ?? ''}`.trim() && !option.url) { add(`${named(idx)} has no content`); }
        });
        const correct = options.filter(o => o?.isCorrect).length;
        if (!correct) { add('no option is marked correct'); }
        const isDropdown = ['custom_dropdown', 'dropdown'].includes(element.displayStyle);
        if ((!element.isMultiSelect || isDropdown) && correct > 1) { add(`single select item has ${correct} correct options`); }
        const matrix: IScoreMatrix = element.scoreMatrix;
        if (!matrix?.isConfirmed) {
          add('score matrix is not confirmed');
          return;
        }
        const present = options.filter(o => !!o);
        if ((matrix.rows?.length ?? 0) !== present.length) {
          add(`score matrix has ${matrix.rows?.length ?? 0} rows for ${present.length} options`);
          return;
        }
        present.forEach((option, idx) => {
          const expected = option.isCorrect ? 1 : 0;
          const actual = matrix.values?.[idx]?.[0]?.value;
          if (+actual !== expected) { add(`${named(idx)} is scored ${actual ?? 'blank'} in the matrix but ${option.isCorrect ? 'is' : 'is not'} marked correct`); }
        });
      });
  });
  return issues;
};

/**
 * Dry run of the option validation: applies the fix to a copy of each item and lists the items that would
 * still have option problems afterwards. The items themselves are never changed.
 */
export const previewMcqOptionIssues = (
  questions: IQuestionConfig[],
  applyFix: (copy: IQuestionConfig) => void,
): { label: string, reasons: string[] }[] => {
  const out: { label: string, reasons: string[] }[] = [];
  (questions ?? []).forEach(question => {
    if (!question) { return; }
    const copy: IQuestionConfig = JSON.parse(JSON.stringify(question));
    try {
      applyFix(copy);
    } catch (e) {
      out.push({ label: question.label, reasons: ['the fix could not be applied'] });
      return;
    }
    const reasons = validateMcqOptionsAgainstMatrix(copy).map(i => `${i.lang.toUpperCase()}: ${i.detail}`);
    if (reasons.length) { out.push({ label: question.label, reasons }); }
  });
  return out;
};

/**
 * Audits run in French hold French-only copies (French content as `content`, no `langLink`).
 * Resolve each flagged item to its full item bank question so both languages are planned and tagged correctly.
 */
export const resolveFlaggedQuestions = (
  flagged: IQuestionConfig[],
  getFullQuestion: (id: number) => IQuestionConfig | undefined,
): IQuestionConfig[] => {
  const seen = new Set<number>();
  const resolved: IQuestionConfig[] = [];
  (flagged ?? []).forEach(question => {
    if (!question || seen.has(question.id)) {
      return;
    }
    seen.add(question.id);
    resolved.push(getFullQuestion(question.id) ?? question);
  });
  return resolved;
};

/**
 * Items the auto-fix opens: any item with at least one fixable matrix in either language, then items
 * with flagged expected answers (re-validated only). Each item once, in that order.
 */
export const selectScoreMatrixFixQuestions = (
  questions: IQuestionConfig[],
  plan: IMatrixReconfirmPlan,
  opts: { includeUnconfirmed: boolean, unconfirmedFilter?: (question: IQuestionConfig) => boolean, includeChanged?: boolean, initializeOnly?: boolean, confirmInitialized?: boolean, flaggedAnswerQuestions?: IQuestionConfig[] },
): IQuestionConfig[] => {
  const fixClasses = [EMatrixReconfirmClass.RECONFIRM_SAFE];
  if (opts.includeUnconfirmed) { fixClasses.push(EMatrixReconfirmClass.UNCONFIRMED); }
  if (opts.includeChanged) { fixClasses.push(EMatrixReconfirmClass.CHANGED); }
  const byId = new Map((questions ?? []).filter(q => !!q).map(q => [q.id, q] as [number, IQuestionConfig]));
  const unconfirmedAllowed = (id: number) => !opts.unconfirmedFilter || !byId.get(id) || opts.unconfirmedFilter(byId.get(id));
  const fixableIds = new Set(plan.entries
    .filter(e => fixClasses.includes(e.cls) && (e.cls !== EMatrixReconfirmClass.UNCONFIRMED || unconfirmedAllowed(e.questionId)))
    // an initialize run only opens items with something to initialize, plus initialized ones when asked to confirm them
    .filter(e => !(opts.initializeOnly && e.cls === EMatrixReconfirmClass.UNCONFIRMED && !e.neverInitialized && !opts.confirmInitialized))
    .map(e => e.questionId));
  const selected: IQuestionConfig[] = [];
  const seen = new Set<number>();
  const add = (question: IQuestionConfig) => {
    if (question && !seen.has(question.id)) {
      seen.add(question.id);
      selected.push(question);
    }
  };
  (questions ?? []).filter(q => fixableIds.has(q?.id)).forEach(add);
  (opts.flaggedAnswerQuestions ?? []).forEach(add);
  return selected;
};
